import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty";
import "core-js/modules/web.dom.iterable";
import "core-js/modules/es6.array.iterator";
import "core-js/modules/es6.object.to-string";
import "core-js/modules/es6.string.iterator";
import "core-js/modules/es6.weak-map";
import { useContext, useMemo, useRef, useReducer } from 'react';
import { useIsomorphicLayoutEffect, useAffectedDebugValue } from './utils';
import { STATE_CONTEXT_PROPERTY, VERSION_CONTEXT_PROPERTY, SUBSCRIBE_CONTEXT_PROPERTY } from './createProvider';
import { createDeepProxy, isDeepChanged, MODE_ASSUME_UNCHANGED_IF_UNAFFECTED, MODE_IGNORE_REF_EQUALITY, MODE_ASSUME_UNCHANGED_IF_UNAFFECTED_IN_DEEP } from './deepProxy';
import { createUseUpdate } from './createUseUpdate';
var MODE_ALWAYS_ASSUME_CHANGED_IF_UNAFFECTED = 0;
var MODE_ALWAYS_ASSUME_UNCHANGED_IF_UNAFFECTED = MODE_ASSUME_UNCHANGED_IF_UNAFFECTED | MODE_ASSUME_UNCHANGED_IF_UNAFFECTED_IN_DEEP;
var MODE_MUTABLE_ROOT_STATE = MODE_IGNORE_REF_EQUALITY; // only for root

var MODE_DEFAULT = MODE_ASSUME_UNCHANGED_IF_UNAFFECTED; // only for root

var STATE_PROPERTY = 's';
var AFFECTED_PROPERTY = 'a';
var CACHE_PROPERTY = 'c';
var DEEP_PROXY_MODE_PROPERTY = 'd';
export var createUseTrackedState = function createUseTrackedState(context) {
  var useTrackedState = function useTrackedState() {
    var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    var _useContext = useContext(context),
        state = _useContext[STATE_CONTEXT_PROPERTY],
        version = _useContext[VERSION_CONTEXT_PROPERTY],
        subscribe = _useContext[SUBSCRIBE_CONTEXT_PROPERTY];

    var affected = new WeakMap();
    var lastTracked = useRef();
    useIsomorphicLayoutEffect(function () {
      var _lastTracked$current;

      lastTracked.current = (_lastTracked$current = {}, _defineProperty(_lastTracked$current, STATE_PROPERTY, state), _defineProperty(_lastTracked$current, AFFECTED_PROPERTY, affected), _defineProperty(_lastTracked$current, CACHE_PROPERTY, new WeakMap()), _defineProperty(_lastTracked$current, DEEP_PROXY_MODE_PROPERTY, opts.unstable_forceUpdateForStateChange ? MODE_ALWAYS_ASSUME_CHANGED_IF_UNAFFECTED : opts.unstable_ignoreIntermediateObjectUsage ? MODE_ALWAYS_ASSUME_UNCHANGED_IF_UNAFFECTED : opts.unstable_ignoreStateEquality ? MODE_MUTABLE_ROOT_STATE :
      /* default */
      MODE_DEFAULT), _lastTracked$current);
    });

    var _useReducer = useReducer(function (c, v) {
      if (version < v) {
        return c + 1; // schedule update
      }

      try {
        var lastTrackedCurrent = lastTracked.current;

        if (lastTrackedCurrent[STATE_PROPERTY] === state || !isDeepChanged(lastTrackedCurrent[STATE_PROPERTY], state, lastTrackedCurrent[AFFECTED_PROPERTY], lastTrackedCurrent[CACHE_PROPERTY], lastTrackedCurrent[DEEP_PROXY_MODE_PROPERTY])) {
          // not changed
          return c; // bail out
        }
      } catch (e) {// ignored (thrown promise or some other reason)
      }

      return c + 1;
    }, 0),
        _useReducer2 = _slicedToArray(_useReducer, 2),
        checkUpdate = _useReducer2[1];

    useIsomorphicLayoutEffect(function () {
      var callback = function callback(nextVersion, nextState) {
        try {
          var lastTrackedCurrent = lastTracked.current;

          if (nextState && (lastTrackedCurrent[STATE_PROPERTY] === nextState || !isDeepChanged(lastTrackedCurrent[STATE_PROPERTY], nextState, lastTrackedCurrent[AFFECTED_PROPERTY], lastTrackedCurrent[CACHE_PROPERTY], lastTrackedCurrent[DEEP_PROXY_MODE_PROPERTY]))) {
            // not changed
            return;
          }
        } catch (e) {// ignored (thrown promise or some other reason)
        }

        checkUpdate(nextVersion);
      };

      var unsubscribe = subscribe(callback);
      return unsubscribe;
    }, [subscribe]);

    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useAffectedDebugValue(state, affected);
    }

    var proxyCache = useRef(new WeakMap()); // per-hook proxyCache

    return createDeepProxy(state, affected, proxyCache.current);
  };

  return useTrackedState;
};
export var createUseTracked = function createUseTracked(context) {
  var useTrackedState = createUseTrackedState(context);
  var useUpdate = createUseUpdate(context);

  var useTracked = function useTracked(opts) {
    var state = useTrackedState(opts);
    var update = useUpdate();
    return useMemo(function () {
      return [state, update];
    }, [state, update]);
  };

  return useTracked;
};